import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from 'assets/iconComponents/SearchIcon';
import { palette } from 'view/themes/palette';
import * as S from './styled';

const SCROLL_PREFETCH_RATIO = 0.8;
// Задержка перед показом прелоадера, чтобы он не мигал на каждой введённой букве —
// спиннер появляется только после короткой паузы в наборе.
const SEARCH_SPINNER_DELAY = 500;

/**
 * Options come from the backend, so only `id` is required (it is used as the React key) — the
 * remaining fields are read by the caller's `getOptionLabel` and `renderOption`.
 * There is deliberately no index signature: with one, only type aliases would satisfy the
 * constraint, and interfaces would not.
 */
export type TAutocompleteOption = {
  id?: string | number;
};

/** Response of a paginated endpoint: one page of options plus the total count. */
export type TAutocompletePaginatedData<T> = {
  data?: T[];
  total?: number;
};

export type TAutocompleteProps<T extends TAutocompleteOption = any> = {
  label?: string;
  value: T | null;
  onChange: (value: T | null) => void;
  getOptionLabel: (option: T) => string;
  /** Search is driven from outside: the component only reports input, the wrapper does the request. */
  inputValue: string;
  setInputValue: (value: string) => void;
  /** Scroll paging: the component increments the page and resets it to 1 on a new search. */
  setPage: Dispatch<SetStateAction<number>>;
  data?: TAutocompletePaginatedData<T> | null;
  isLoading?: boolean;
  marginbottom?: number;
  /**
   * Custom rendering for the option body instead of the plain `getOptionLabel` string.
   * The checkmark of the selected option is still drawn by the component itself.
   */
  renderOption?: (option: T, isSelected: boolean) => ReactNode;
  /** Passed by call sites but not read by the component. */
  name?: string;
  page?: number;
};

const Autocomplete = <T extends TAutocompleteOption = any>({
  label = 'Select...',
  value,
  onChange,
  getOptionLabel,
  inputValue,
  setInputValue,
  setPage,
  data,
  isLoading,
  marginbottom = 0,
  // Aliased to avoid confusion with MUI's prop of the same name below.
  renderOption: renderOptionContent,
}: TAutocompleteProps<T>) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const isFetchingRef = useRef(false);
  // isSearchDelaying — печатаем, но спиннер ещё не показываем (ждём паузу).
  // isSearchPending — пауза выдержана, крутим спиннер до прихода данных.
  const [isSearchDelaying, setIsSearchDelaying] = useState(false);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const searchDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const nextOptions = data?.data;

    if (Array.isArray(nextOptions)) {
      setOptions((prev) => {
        // Догрузка следующей страницы при скролле — дописываем; новый поиск — заменяем.
        return isFetchingRef.current ? [...prev, ...nextOptions] : nextOptions;
      });
      setTotal(data?.total ?? 0);
    }
    isFetchingRef.current = false;
    if (searchDelayTimerRef.current) {
      clearTimeout(searchDelayTimerRef.current);
      searchDelayTimerRef.current = null;
    }
    setIsSearchDelaying(false);
    setIsSearchPending(false);
  }, [data]);

  useEffect(() => {
    return () => {
      if (searchDelayTimerRef.current) {
        clearTimeout(searchDelayTimerRef.current);
      }
    };
  }, []);

  const handleListboxScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const listbox = event.currentTarget;
      const scrolledRatio = listbox.scrollTop / (listbox.scrollHeight - listbox.clientHeight || 1);
      const isNearBottom = scrolledRatio >= SCROLL_PREFETCH_RATIO;

      if (isNearBottom && !isFetchingRef.current && !isLoading && options.length < total) {
        isFetchingRef.current = true;
        setPage((prevPage) => {
          return prevPage + 1;
        });
      }
    },
    [isLoading, options.length, total, setPage]
  );

  return (
    <div style={{ marginBottom: `${marginbottom}px` }}>
      <S.SelectLabel>{label}</S.SelectLabel>
      <MuiAutocomplete
        open={open}
        onOpen={() => {
          return setOpen(true);
        }}
        onClose={() => {
          return setOpen(false);
        }}
        value={value}
        onChange={(_, newValue) => {
          return onChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          setInputValue(newInputValue);
          if (reason === 'input') {
            setPage(1);
            // Список НЕ обнуляем сразу — пока нет прелоадера, показываем прошлые результаты.
            // Сбрасываем таймер на каждой букве — спиннер появится только после паузы.
            if (searchDelayTimerRef.current) {
              clearTimeout(searchDelayTimerRef.current);
            }
            setIsSearchPending(false);
            setIsSearchDelaying(true);
            searchDelayTimerRef.current = setTimeout(() => {
              // Пауза выдержана: чистим прошлый список и показываем спиннер, пока летит запрос.
              setOptions([]);
              setIsSearchDelaying(false);
              setIsSearchPending(true);
            }, SEARCH_SPINNER_DELAY);
          }
        }}
        getOptionLabel={getOptionLabel}
        filterOptions={(x) => {
          return x;
        }}
        options={options}
        loading={isLoading || isSearchPending}
        loadingText={<CircularProgress size={24} sx={{ display: 'block', margin: '8px auto' }} />}
        // Пока ждём паузу в наборе — не показываем "No options", чтобы не мигало.
        noOptionsText={isSearchDelaying ? '' : 'No options'}
        renderOption={(props, option, { selected }) => {
          // key arrives inside props, so pull it out — React 19 warns on spreading a key.
          const { key, ...rest } = props as typeof props & { key: string };

          return (
            <S.AutocompleteOption {...rest} key={option.id ?? key} isSelected={selected}>
              {renderOptionContent ? renderOptionContent(option, selected) : getOptionLabel(option)}

              {selected && (
                <S.Checkmark>
                  <CheckIcon />
                </S.Checkmark>
              )}
            </S.AutocompleteOption>
          );
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder="Select..."
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: <>{params.InputProps.endAdornment}</>,
                  startAdornment: (
                    <>
                      <SearchIcon />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                },
              }}
              sx={{
                '.MuiInputBase-root': {
                  minHeight: '40px',
                  color: palette.text.body,
                  borderColor: `${palette.border.default}`,
                  borderRadius: '8px',
                  padding: '8px 12px',

                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: `${palette.border.default}`,
                    borderRadius: '8px',
                  },

                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.hover.brand,
                  },

                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: palette.primary.purple500,
                  },

                  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.alerts.error,
                  },
                },
              }}
            />
          );
        }}
        sx={{ width: '100%' }}
        popupIcon={<KeyboardArrowDownIcon />}
        slotProps={{
          popupIndicator: {
            sx: {
              color: palette.unknown.icon_grey,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            },
          },
          popper: { sx: { borderRadius: '8px' } },
          paper: {
            sx: {
              marginTop: '2px',
              borderRadius: '8px',
              boxShadow:
                '0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
              '.MuiAutocomplete-noOptions': { padding: '8px 12px' },
            },
          },
          listbox: {
            onScroll: handleListboxScroll,
            sx: {
              border: 'none',
              borderRadius: '8px',
              maxHeight: '245px',
              overflowY: 'auto',
              padding: '0',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': {
                background: '#D8DBE8',
                borderRadius: '10px',
                transition: 'background 0.2s ease-in-out',
              },
              '&::-webkit-scrollbar-thumb:hover': { background: '#BFC3D4' },
              '&::-webkit-scrollbar-thumb:active': { background: '#A5AABF' },
            },
          },
        }}
      />
    </div>
  );
};

export default Autocomplete;
